// src/pages/InsideScienceLearn.jsx
// v22 — Flow upgrades (cool, smooth animations, perf‑safe)
// • Smoother, more varied entrances (staggered, alternating, minimal GPU cost)
// • Section‑flow background now activates only between INSIDE → HOW (less CPU)
// • List bullets glide in from alternating sides
// • Media tiles (figures/video) get one‑time shimmer + gentle scale‑up on reveal
// • Minor hero micro‑parallax for title/kicker (pointer devices only)
// • Keeps centred underlines + pointer cursor for BUY NOW

import React, { useRef, useLayoutEffect, useState, useEffect } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import BuyButton from "./buybutton";
import DopeShotInfo from "./DopeShotInfo";


gsap.registerPlugin(ScrollTrigger);

function SafeImg({ src, alt, className }) {
  const [ok, setOk] = useState(true);
  return ok ? (
    <img className={className} src={src} alt={alt} onError={() => setOk(false)} loading="lazy" decoding="async" />
  ) : (
    <div className={`${className} img-fallback`} aria-label={alt} />
  );
}

export default function InsideScienceLearn() {
  const rootRef = useRef(null);
  const flowRef = useRef(null);
  const roRef = useRef(null);
  const shineTimer = useRef(null);

  useLayoutEffect(() => {
    const ctx = gsap.context(() => {
      const root = rootRef.current;
      if (!root) return;

      root.classList.add("js-ready");

      const hasMM = typeof window !== "undefined" && typeof window.matchMedia === "function";
      const prefersReduced = hasMM ? window.matchMedia("(prefers-reduced-motion: reduce)").matches : false;
      const isPointer = hasMM ? window.matchMedia("(pointer: fine)").matches : false;

      /* ---------- Top progress ---------- */
      gsap.set(".page-progress .bar", { scaleX: 0, transformOrigin: "0% 50%" });
      ScrollTrigger.create({ start: 0, end: "max", onUpdate: s => gsap.set(".page-progress .bar", { scaleX: s.progress }) });

      /* ---------- Helper: mark reveal targets AFTER JS ---------- */
      const addRevealAttr = (selector, attr = "data-reveal") => {
        gsap.utils.toArray(selector).forEach((el) => (el instanceof Element) && el.setAttribute(attr, ""));
      };

      // Containers
      addRevealAttr([ ".isl-inside .i-body", ".isl-why .w-copy", ".isl-works .wk-copy", ".isl-how .t-grid", ".isl-whyitworks .wi-copy", ".isl-cta .cta-wrap" ]);
      // Hero chunks
      addRevealAttr([ ".isl-hero .h-kicker", ".isl-hero .h-title", ".isl-hero .h-cue" ]);
      // HOW cards
      addRevealAttr([ ".isl-how .t-card" ]);
      // Children
      addRevealAttr([ ".isl-inside .i-copy > *", ".isl-why .w-copy > *", ".isl-works .wk-copy > *", ".isl-whyitworks .wi-copy > *", ".isl-cta .cta-wrap > p" ], "data-reveal-child");

      const animateReveal = (el, { y = 22, dur = 0.56, rotX = 5, scl = 0.99 } = {}) => {
        gsap.fromTo(el, { y, autoAlpha: 0, rotateX: rotX, scale: scl }, {
          y: 0, autoAlpha: 1, rotateX: 0, scale: 1, duration: dur, ease: "power3.out",
          onComplete: () => { el.removeAttribute("data-reveal"); gsap.set(el, { clearProps: "transform,opacity" }); }
        });
      };

      /* ---------- HERO entrance + sheen + parallax ---------- */
      const hero = root.querySelector(".isl-hero");
      if (hero) {
        const heroEls = gsap.utils.toArray([".isl-hero .h-kicker", ".isl-hero .h-title", ".isl-hero .h-cue"]);
        heroEls.forEach((el, i) => {
          ScrollTrigger.create({ trigger: hero, start: "top 82%", once: true, onEnter: () => animateReveal(el, { y: 22, dur: 0.5 + i * 0.06, rotX: 4, scl: 0.995 }) });
        });

        const sheen = hero.querySelector(".h-sheen");
        if (sheen) ScrollTrigger.create({ trigger: hero, start: "top 82%", once: true, onEnter: () => gsap.to(sheen, { xPercent: 120, opacity: 1, duration: 1.0, ease: "power2.out" }) });

        if (!prefersReduced) {
          const bg = hero.querySelector(".h-bg");
          if (bg) ScrollTrigger.create({ trigger: hero, start: "top bottom", end: "bottom top", scrub: true, onUpdate: s => gsap.set(bg, { yPercent: gsap.utils.mapRange(0,1,-6,6,s.progress), scale: 1.05 }) });

          // Micro-parallax for title/kicker on pointer devices
          if (isPointer) {
            const title = hero.querySelector(".h-title");
            const kicker = hero.querySelector(".h-kicker");
            const setT = title ? gsap.quickSetter(title, "yPercent") : null;
            const setK = kicker ? gsap.quickSetter(kicker, "yPercent") : null;
            let raf = 0;
            const onMove = (e) => {
              if (!setT && !setK) return;
              if (raf) return; // throttle to rAF
              raf = requestAnimationFrame(() => {
                const r = hero.getBoundingClientRect();
                const relY = ((e.clientY - r.top) / Math.max(1, r.height) - 0.5) * 2; // -1..1
                setT && setT(relY * -1.2);
                setK && setK(relY * -2);
                raf = 0;
              });
            };
            hero.addEventListener("mousemove", onMove);
          }
        }
      }

      /* ---------- Headings underline sweep ---------- */
      gsap.utils.toArray(["#inside-title", ".i-title", ".w-title", ".wk-title", "#how-title", "#wi-title"]).forEach((h) => {
        if (!(h instanceof Element)) return;
        ScrollTrigger.create({ trigger: h, start: "top 92%", once: true, onEnter: () => h.classList.add("reveal-line") });
      });

      /* ---------- Containers ---------- */
      gsap.utils.toArray('[data-reveal]').forEach((el, idx) => {
        ScrollTrigger.create({
          trigger: el,
          start: "top 88%",
          once: true,
          onEnter: () => animateReveal(el, { y: 24, dur: 0.58 + (idx % 3) * 0.04, rotX: 5, scl: 0.992 })
        });
      });

      /* ---------- Child text batch (stagger) ---------- */
      ScrollTrigger.batch('[data-reveal-child]', {
        start: "top 92%",
        once: true,
        interval: 0.08,
        batchMax: 8,
        onEnter: (els) => {
          els.forEach((el, i) => gsap.fromTo(el, { y: 10, autoAlpha: 0 }, {
            y: 0, autoAlpha: 1, duration: 0.42 + (i%4)*0.02, ease: "power3.out",
            onComplete: () => { el.removeAttribute("data-reveal-child"); gsap.set(el, { clearProps: "transform,opacity" }); }
          }));
        }
      });

      /* ---------- Alternating list items (WHY list) ---------- */
      gsap.utils.toArray('.w-list li').forEach((li, i) => {
        ScrollTrigger.create({
          trigger: li,
          start: "top 92%",
          once: true,
          onEnter: () => gsap.fromTo(li, { x: i % 2 ? 16 : -16, autoAlpha: 0 }, { x: 0, autoAlpha: 1, duration: 0.5, ease: "power3.out" })
        });
      });

      /* ---------- Media reveal: slight scale pop + shimmer ---------- */
      gsap.utils.toArray([".i-ph", ".w-hero", ".wk-hero", ".t-media"]).forEach((box) => {
        ScrollTrigger.create({
          trigger: box,
          start: "top 90%",
          once: true,
          onEnter: () => {
            gsap.fromTo(box, { scale: 0.985, autoAlpha: 0 }, { scale: 1, autoAlpha: 1, duration: 0.5, ease: "power3.out" });
            box.classList.add("shimmer-run");
          }
        });
      });

      /* ---------- Parallax flow (scrubbed, perf‑safe) ---------- */
      if (!prefersReduced) {
        const targets = gsap.utils.toArray([".i-ph .i-img", ".w-img", ".wk-vid", ".wk-img", ".t-media .t-img"]);
        targets.forEach((el, i) => {
          if (!(el instanceof Element)) return;
          const wrap = el.closest("figure, .t-media, .i-ph");
          const setter = gsap.quickSetter(el, "yPercent");
          ScrollTrigger.create({
            trigger: wrap || el,
            start: "top bottom",
            end: "bottom top",
            scrub: true,
            onUpdate: (s) => setter(gsap.utils.mapRange(0, 1, i % 2 ? -3 : -6, i % 2 ? 3 : 6, s.progress))
          });
        });
      }

      /* ---------- HOW rail (sticky progress) ---------- */
      const steps = root.querySelector(".isl-how .t-steps");
      const fill = root.querySelector(".isl-how .t-fill");
      if (steps && fill) {
        gsap.set(fill, { scaleY: 0, transformOrigin: "0% 0%" });
        ScrollTrigger.create({ trigger: steps, start: "top center", end: "bottom center", onUpdate: s => gsap.set(fill, { scaleY: s.progress }) });
        gsap.utils.toArray(".isl-how .t-step").forEach((step) => {
          if (!(step instanceof Element)) return;
          const card = step.querySelector(".t-card");
          const dot = step.querySelector(".t-dot");
          if (card && card.hasAttribute("data-reveal")) {
            ScrollTrigger.create({ trigger: step, start: "top 85%", once: true, onEnter: () => animateReveal(card, { y: 22, dur: 0.55, rotX: 5, scl: 0.99 }) });
          }
          if (dot) {
            ScrollTrigger.create({ trigger: step, start: "top 60%", end: "bottom 60%", onEnter: () => step.classList.add("is-active"), onEnterBack: () => step.classList.add("is-active"), onLeave: () => step.classList.remove("is-active"), onLeaveBack: () => step.classList.remove("is-active") });
            ScrollTrigger.create({ trigger: step, start: "top 80%", once: true, onEnter: () => gsap.fromTo(dot, { scale: 0.6 }, { scale: 1, duration: 0.28, ease: "back.out(3)" }) });
          }
        });
      }

      /* ---------- Flow band bounds & activation ---------- */
      const inside = root.querySelector(".isl-inside");
      const how = root.querySelector(".isl-how");
      const sectionFlow = flowRef.current;
      function setFlowBounds() {
        if (!inside || !how || !sectionFlow) return;
        const mainTop = root.getBoundingClientRect().top + window.scrollY;
        const insideTop = inside.getBoundingClientRect().top + window.scrollY - mainTop;
        const howBottom = how.getBoundingClientRect().bottom + window.scrollY - mainTop;
        const height = Math.max(0, Math.round(howBottom - insideTop));
        sectionFlow.style.top = insideTop + "px";
        sectionFlow.style.height = height + "px";
      }
      setFlowBounds();

      const RO = typeof window !== "undefined" ? window.ResizeObserver : undefined;
      if (RO) {
        const ro = new RO(() => { setFlowBounds(); ScrollTrigger.refresh(); });
        roRef.current = ro;
        if (inside) ro.observe(inside);
        if (how) ro.observe(how);
      }

      // Activate flow only when between the two sections
      if (inside && how && sectionFlow) {
        ScrollTrigger.create({
          trigger: inside,
          start: "top bottom",
          endTrigger: how,
          end: "bottom top",
          onEnter: () => sectionFlow.classList.add("is-visible"),
          onEnterBack: () => sectionFlow.classList.add("is-visible"),
          onLeave: () => sectionFlow.classList.remove("is-visible"),
          onLeaveBack: () => sectionFlow.classList.remove("is-visible")
        });
      }

      if (prefersReduced) root.classList.add("bg-lite");

      const onLoad = () => ScrollTrigger.refresh();
      window.addEventListener("load", onLoad);
      requestAnimationFrame(() => ScrollTrigger.refresh());
    }, rootRef);

    return () => {
      try { roRef.current && roRef.current.disconnect(); } catch {}
      if (shineTimer.current) clearInterval(shineTimer.current);
      const root = rootRef.current; if (root) root.classList.remove("js-ready");
      ctx.revert();
    };
  }, []);

  /* Occasional hero title shine */
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const title = root.querySelector(".h-title");
    if (!title) return;
    const kick = () => { title.classList.remove("shine-run"); void title.offsetWidth; title.classList.add("shine-run"); };
    requestAnimationFrame(kick);
    shineTimer.current = setInterval(kick, 9000);
    return () => { if (shineTimer.current) clearInterval(shineTimer.current); };
  }, []);

  const scrollToInside = () => { const el = document.getElementById("inside-title"); if (el) el.scrollIntoView({ behavior: "smooth", block: "start" }); };

  return (
    <main className="inside-science-learn v22" ref={rootRef}>
        
      {/* Fixed global background (mono, static) */}
      <div className="bg-stage" aria-hidden>
        <div className="bg-mesh" />
        <div className="bg-grid" />
        <div className="bg-noise" />
      </div>

      {/* Progress */}
      <div className="page-progress" aria-hidden><span className="bar" /></div>

      {/* HERO */}
      <section className="isl-hero" aria-label="The science of fuelling with Dope Shot">
        <div className="h-bg" style={{ backgroundImage: "url(/DSCF9477.webp)" }} aria-hidden />
        <div className="h-aurora" aria-hidden>
          <span className="blob b1" />
          <span className="blob b2" />
        </div>
        {/* shiny prism layer */}
        <div className="h-shine" aria-hidden>
          <span className="laser l1" />
          <span className="laser l2" />
          <span className="laser l3" />
        </div>
        <div className="h-overlay" aria-hidden />
        <div className="h-content">
          <p className="h-kicker">THE NEW FUEL TYPE{/*SIMPLE HABITS THAT MAKE HARD DAYS EASIER*/}</p>
          <h1 className="h-title">The science of fuelling with Dope Shot</h1>
          <button className="h-cue" type="button" onClick={scrollToInside} aria-describedby="inside-title" title="Scroll to ‘What’s really inside this bottle?’">﹀</button>
        </div>
        <span className="h-sheen" aria-hidden />
        <div className="h-sparkles" aria-hidden>
          <i className="sp s1"/><i className="sp s2"/><i className="sp s3"/>
        </div>
      </section>

      {/* FLOW BACKGROUND spanning INSIDE → HOW */}
      <div className="section-flow" ref={flowRef} aria-hidden>
        <span className="line l1" />
        <span className="line l2" />
        <span className="line l3" />
        <span className="line l4" />
        <span className="flow-sheen" />
      </div>

      {/* WHAT’S REALLY INSIDE THIS BOTTLE? */}
      <section className="isl-inside" aria-labelledby="inside-title">
        <header className="i-head">
          <h2 id="inside-title" className="i-title">INSIDE THE BOTTLE</h2>
        </header>
        <div className="i-body">
          <div className="i-copy">
            <p>Dope Shot is made from maltodextrin, glucose, fructose, sucrose, sea salt, and citric acid.</p>
            <p>These four carbohydrates form  Multiple Transportable Carbohydrates (MTC) — a blend that helps your body absorb more energy with less gut strain.</p>
            <p>Studies show MTC blends increase carbohydrate use by 20 to 50 percent compared to single-carb formulas.</p>
            <p>Resulting in consistent energy with fewer stomach issues, even during long, intense sessions.</p>
            {/*<blockquote>“You’re not forcing your body to work harder. You’re helping it work smarter.”</blockquote>*/}
          </div>
          <div className="i-collage" aria-label="Images">
            <figure className="i-ph p1 frame-shimmer"><SafeImg className="i-img" src="/DSCF0216.webp" alt="Dope Shot mix" /></figure>
            {/*<figure className="i-ph p2 frame-shimmer"><SafeImg className="i-img" src="/DSCF0103.JPG" alt="Ride session" /></figure>
            <figure className="i-ph p3 frame-shimmer"><SafeImg className="i-img" src="/eDSCF0379.jpg" alt="Lab bench" /></figure>*/}
          </div>
        </div>
      </section>

          <hr className="hr-shine" />
        
      <DopeShotInfo />


      <style>{`
        .inside-science-learn{--bg:#0a0a0a;--fg:#f4f4f4;--muted:#b9b9b9;--line:rgba(255,255,255,.14);--line2:rgba(255,255,255,.08);--soft:rgba(255,255,255,.06);--card:#101113;color-scheme:dark;background:var(--bg);color:var(--fg);-webkit-font-smoothing:antialiased;-moz-osx-font-smoothing:grayscale;position:relative;overflow-x:hidden}
        section{padding:clamp(52px,7vw,110px) 0}
        p{line-height:1.75;color:var(--muted);margin:0 0 10px, font-size:clamp(20px,1.2vw,22px)}
        h1,h2,h3{color:var(--fg);margin:0 0 8px;letter-spacing:.15px;position:relative}

        /* --- reveal line (base) --- */
        #inside-title::after, .i-title::after, .w-title::after, .wk-title::after, #how-title::after, #wi-title::after{content:"";position:absolute;left:0;bottom:-6px;height:2px;width:140px;background:linear-gradient(90deg, rgba(255,255,255,.0), rgba(255,255,255,.7), rgba(255,255,255,0));transform-origin:left center;transform:scaleX(0);transition:transform .6s ease}
        #inside-title.reveal-line::after, .i-title.reveal-line::after, .w-title.reveal-line::after, .wk-title.reveal-line::after, #how-title.reveal-line::after, #wi-title.reveal-line::after{transform:scaleX(1)}
        /* centered underline for selected headings */
        #inside-title::after, #how-title::after, #wi-title::after { left:50%; transform-origin:center center; transform:translateX(-50%) scaleX(0); }
        #inside-title.reveal-line::after, #how-title.reveal-line::after, #wi-title.reveal-line::after { transform:translateX(-50%) scaleX(1); }

        .img-fallback{background:linear-gradient(180deg,rgba(255,255,255,.08),rgba(255,255,255,.02)),repeating-linear-gradient(45deg,rgba(255,255,255,.06),rgba(255,255,255,.06) 10px,transparent 10px,transparent 20px)}

        /* Fixed global background — monochrome & static */
        .bg-stage{position:fixed;inset:0;z-index:-1;pointer-events:none}
        .bg-mesh{position:absolute;inset:-10%;background:
          radial-gradient(1200px 700px at 55% 40%, rgba(255,255,255,.10), rgba(255,255,255,0) 55%),
          radial-gradient(1000px 600px at 80% 25%, rgba(255,255,255,.08), rgba(255,255,255,0) 58%),
          radial-gradient(900px 600px at 20% 80%, rgba(255,255,255,.07), rgba(255,255,255,0) 60%),
          linear-gradient(180deg,#060606 0%, #0a0a0a 60%, #0a0a0a 100%)}
        .bg-grid{position:absolute;inset:0;opacity:.18;background:
          linear-gradient(transparent 95%, rgba(255,255,255,.05) 96%) 0 0/100% 36px,
          linear-gradient(90deg, transparent 95%, rgba(255,255,255,.05) 96%) 0 0/36px 100%}
        .bg-noise{position:absolute;inset:0;opacity:.10;mix-blend-mode:overlay;background:
          radial-gradient(rgba(255,255,255,.02) 1px, transparent 1px) 0 0/3px 3px,
          radial-gradient(rgba(255,255,255,.01) .8px, transparent 1px) 1.5px 1.5px/3px 3px}

        /* Progress */
        .page-progress{position:sticky;top:0;z-index:70;height:3px;background:transparent}
        .page-progress .bar{height:100%;width:100%;background:linear-gradient(90deg,rgba(255,255,255,0),rgba(255,255,255,.9),rgba(255,255,255,0));transform:scaleX(0);display:block}

        /* HERO */
        .isl-hero{position:relative;min-height:clamp(560px,100vh,820px);display:grid;place-items:center;overflow:hidden;isolation:isolate}
        .isl-hero .h-bg{position:absolute;inset:0;background-size:cover;background-position:center;filter:grayscale(1) contrast(1.06) brightness(.5);will-change:transform}
        .isl-hero .h-aurora{position:absolute;inset:-15% -15%;filter:grayscale(1);pointer-events:none}
        .isl-hero .h-aurora .blob{position:absolute;width:60vmax;height:40vmax;border-radius:50%;background:radial-gradient(60% 40% at 50% 50%, rgba(255,255,255,.10), rgba(255,255,255,0) 60%);will-change:transform}
        .isl-hero .h-aurora .b1{left:10%;top:10%;animation:drift1 50s ease-in-out infinite}
        .isl-hero .h-aurora .b2{right:5%;bottom:0;animation:drift2 60s ease-in-out infinite}
        @keyframes drift1{0%{transform:translate3d(0,0,0) scale(1)}50%{transform:translate3d(6%,4%,0) scale(1.05)}100%{transform:translate3d(0,0,0) scale(1)}}
        @keyframes drift2{0%{transform:translate3d(0,0,0) scale(1)}50%{transform:translate3d(-5%,-3%,0) scale(0.98)}100%{transform:translate3d(0,0,0) scale(1)}}
        .isl-hero .h-shine{position:absolute;inset:-20% -10%;pointer-events:none;mix-blend-mode:screen}
      
        .isl-hero .h-shine .l1{top:32%;transform:rotate(-14deg);animation:laserSweep 11s linear infinite}
        .isl-hero .h-shine .l2{top:56%;transform:rotate(-12deg);animation:laserSweep 13s linear infinite 1.5s}
        .isl-hero .h-shine .l3{top:76%;transform:rotate(-10deg);animation:laserSweep 15s linear infinite 3s}
        @keyframes laserSweep{0%{transform:translateX(-40%) rotate(var(--a, -12deg))}100%{transform:translateX(40%) rotate(var(--a, -12deg))}}
        .isl-hero .h-overlay{position:absolute;inset:0;background:radial-gradient(90% 70% at 50% 30%,rgba(255,255,255,.06) 0%,transparent 60%),linear-gradient(to top,rgba(0,0,0,.65),transparent 42%)}
        .isl-hero .h-content{position:relative;text-align:center;width:min(1000px,92vw);padding:0 16px}
         .isl-hero .h-kicker{text-transform:uppercase;letter-spacing:.16em;font-size:2.48rem;color:var(--muted);margin-bottom:10px}
        .isl-hero .h-title{text-transform:uppercase;font-size:clamp(45px,6.2vw,70px);line-height:1.06;margin-bottom:10px;text-shadow:0 6px 28px rgba(0,0,0,.6);position:relative;display:inline-block}
        /* Periodic shiny sweep over hero title */
        .isl-hero .h-title::after{content:"";position:absolute;inset:-10% -20%;background:linear-gradient(115deg, transparent 30%, rgba(255,255,255,.35) 50%, transparent 70%);mix-blend-mode:screen;transform:translateX(-120%) skewX(-12deg);opacity:0;pointer-events:none}
        .isl-hero .h-title.shine-run::after{animation:titleShine 2s ease-out forwards}
        @keyframes titleShine{0%{transform:translateX(-120%) skewX(-12deg);opacity:0}35%{opacity:.6}60%{opacity:.2}100%{transform:translateX(120%) skewX(-12deg);opacity:0}}

        /* Sparkles (very light) */
        .h-sparkles{position:absolute;inset:0;pointer-events:none}
        .h-sparkles .sp{position:absolute;width:6px;height:6px;border-radius:50%;background:radial-gradient(circle at 40% 40%, #fff, rgba(255,255,255,.3));filter:drop-shadow(0 0 8px rgba(255,255,255,.5));opacity:.0}
        .h-sparkles .s1{left:20%;top:35%;animation:twinkle 5s ease-in-out infinite .6s}
        .h-sparkles .s2{left:70%;top:25%;animation:twinkle 6s ease-in-out infinite 1.2s}
        .h-sparkles .s3{left:55%;top:70%;animation:twinkle 7s ease-in-out infinite 2s}
        @keyframes twinkle{0%,100%{opacity:0;transform:scale(.6)}40%{opacity:.7;transform:scale(1)}60%{opacity:.25;transform:scale(.8)}}

        .isl-hero .h-cue{margin:16px auto 0;width:46px;height:46px;display:grid;place-items:center;border:1px solid var(--line);border-radius:999px;color:var(--muted);background:rgba(0,0,0,.15);backdrop-filter:blur(1px);cursor:pointer;animation:cue-bounce 1.9s ease-in-out infinite;font-size:34px;line-height:1;transition:background .3s,color .3s;font-weight:900}
        @keyframes cue-bounce{0%,100%{transform:translateY(0);opacity:.9}50%{transform:translateY(6px);opacity:.65}}
        .isl-hero .h-sheen{position:absolute;inset:-8% -20%;background:linear-gradient(115deg,transparent 30%,rgba(255,255,255,.18) 48%,transparent 66%);mix-blend-mode:screen;opacity:0}

        /* Section Flow (INSIDE → HOW) */
        .section-flow{position:absolute;left:0;right:0;top:0;height:0;z-index:0;pointer-events:none}
        .section-flow .line{position:absolute;left:-10%;right:-10%;height:1px;background:linear-gradient(90deg,transparent,rgba(255,255,255,.20),transparent);opacity:.35;will-change:transform}
        .section-flow .l1{top:15%;animation:slither 36s linear infinite}
        .section-flow .l2{top:40%;animation:slither 40s linear infinite reverse}
        .section-flow .l3{top:65%;animation:slither 34s linear infinite}
        .section-flow .l4{top:85%;animation:slither 42s linear infinite reverse}
        .section-flow .flow-sheen{position:absolute;left:-30%;right:-30%;top:0;bottom:0;background:linear-gradient(90deg,transparent,rgba(255,255,255,.10),transparent);transform:translate3d(-50%,0,0);will-change:transform;animation:sweep 28s linear infinite}
        @keyframes slither{0%{transform:translate3d(-6%,0,0)}100%{transform:translate3d(6%,0,0)}}
        @keyframes sweep{0%{transform:translate3d(-60%,0,0)}100%{transform:translate3d(60%,0,0)}}
        .section-flow:not(.is-visible) .line, .section-flow:not(.is-visible) .flow-sheen{animation-play-state: paused}

        /* INSIDE */
        .isl-inside .i-head{text-align:center;margin-bottom:30px;position:relative;z-index:1}
        .isl-inside .i-title{font-size:clamp(24px,4.8vw,42px)}
        .isl-inside .i-body{width:min(1160px,92vw);margin:0 auto;display:grid;gap:clamp(20px,4vw,42px);grid-template-columns:1.1fr .9fr;position:relative;z-index:1}
        .isl-inside blockquote{margin:12px 0 0;padding-left:14px;border-left:2px solid var(--line);color:var(--fg);font-weight:600}
        .isl-inside .i-collage{display:grid;gap:14px;grid-template-columns:1fr 1fr;align-content:start}
        .isl-inside .i-ph{position:relative;overflow:hidden;border-radius:18px;background:var(--card);outline:1px solid var(--line2);box-shadow:0 0 0 1px var(--soft) inset}
        .isl-inside .i-ph.p1{grid-column:1/-1;aspect-ratio:16/9}
        .isl-inside .i-ph.p2,.isl-inside .i-ph.p3{aspect-ratio:4/3}
        .i-img,.w-img,.wk-img,.t-img{width:100%;height:100%;object-fit:cover;display:block;will-change:transform}

        /* One-time frame shimmer */
        .frame-shimmer::after{content:"";position:absolute;inset:-10% -30%;background:linear-gradient(120deg,transparent 30%, rgba(255,255,255,.14) 50%, transparent 70%);transform:translateX(-120%) skewX(-12deg);opacity:0;pointer-events:none}
        .frame-shimmer.shimmer-run::after{animation:frameShimmer 1.15s ease-out forwards}
        @keyframes frameShimmer{0%{transform:translateX(-120%) skewX(-12deg);opacity:0}40%{opacity:.5}80%{opacity:.15}100%{transform:translateX(120%) skewX(-12deg);opacity:0}}

        /* WHY */
        .isl-why{width:min(1160px,92vw);margin:0 auto;display:grid;gap:clamp(20px,4vw,40px);grid-template-columns:1.05fr .95fr;align-items:center;position:relative;z-index:1}
        .isl-why .w-title{font-size:clamp(24px,4.6vw,40px);margin:0 0 10px}
        .isl-why .w-list{list-style:none;padding:0;margin:12px 0 0;display:grid;gap:12px}
        .isl-why .w-list li{display:grid;grid-template-columns:28px 1fr;gap:10px;align-items:start;color:var(--fg)}
        .isl-why .w-icon{width:22px;height:22px;display:inline-grid;place-items:center;border-radius:6px;background:rgba(255,255,255,.06);outline:1px solid var(--line2);box-shadow:0 0 0 1px var(--soft) inset;color:#fff;opacity:0.9}
        .isl-why .w-icon svg{width:16px;height:16px}
        .isl-why .w-hero{position:relative;border-radius:18px;overflow:hidden;outline:1px solid var(--line2);box-shadow:0 10px 30px rgba(0,0,0,.35)}

        /* WORKS */
        .isl-works{width:min(1160px,92vw);margin:0 auto;display:grid;gap:clamp(18px,3.6vw,36px);grid-template-columns:1.05fr .95fr;align-items:center;position:relative;z-index:1}
        .wk-title{font-size:clamp(22px,4.2vw,36px);margin:0 0 8px}
        .wk-hero{position:relative;border-radius:18px;overflow:hidden;outline:1px solid var(--line2);box-shadow:0 10px 30px rgba(0,0,0,.35)}
        .wk-vid{width:100%;height:100%;object-fit:cover;display:block}

        /* HOW */
        .isl-how{width:min(1160px,92vw);margin:0 auto;position:relative;z-index:1}
        .isl-how .h-head{text-align:center;margin-bottom:28px}
        .isl-how .h-title{font-size:clamp(22px,4.4vw,38px)}
        .isl-how .t-grid{display:grid;grid-template-columns:80px 1fr;gap:16px;position:relative}
        .isl-how .t-rail{position:sticky;top:110px;height:min(70vh,640px);margin-left:39px}
        .isl-how .t-rail::before{content:"";position:absolute;left:-1px;top:0;bottom:0;border-left:1px solid var(--line)}
        .isl-how .t-rail .t-fill{position:absolute;left:-1px;top:0;width:2px;height:100%;background:#fff;border-radius:2px;box-shadow:0 0 16px rgba(255,255,255,.35);transform:scaleY(0);transform-origin:top;will-change:transform}
        .isl-how .t-steps{display:grid;gap:18px}
        .isl-how .t-step{display:grid;grid-template-columns:80px 1fr;gap:0 16px;align-items:start}
        .isl-how .t-step .t-dot{width:12px;height:12px;border-radius:50%;border:2px solid var(--fg);background:var(--bg);margin-left:34px;margin-top:10px;transition:transform .25s ease,background .25s ease, box-shadow .25s ease}
        .isl-how .t-step.is-active .t-dot{background:var(--fg);transform:scale(1.15);box-shadow:0 0 18px rgba(255,255,255,.35)}
        .isl-how .t-card{background:linear-gradient(180deg,rgba(255,255,255,.05),rgba(255,255,255,.015));border:1px solid var(--line);border-radius:16px;padding:clamp(12px,1.6vw,16px);box-shadow:0 10px 30px rgba(0,0,0,.35);display:grid;grid-template-columns:1fr 1.6fr;gap:16px}
        .isl-how .t-media{border-radius:12px;overflow:hidden;background:var(--card);aspect-ratio:4/3;position:relative}
        .isl-how .t-body h3{margin:4px 0 8px;font-size:clamp(16px,2.6vw,20px);color:var(--fg)}

        /* WHY IT WORKS */
        .isl-whyitworks{text-align:center}
        .isl-whyitworks .wi-copy{width:min(860px,92vw);margin:0 auto}
        .isl-whyitworks .wi-title{font-size:clamp(22px,4vw,36px);margin:0 0 12px}

        /* CTA */
        .isl-cta{position:relative;overflow:hidden;background:linear-gradient(180deg,rgba(255,255,255,.03),rgba(255,255,255,.01))}
        .isl-cta .cta-bg{position:absolute;inset:0;overflow:hidden;pointer-events:none}
        .isl-cta .cta-glow{position:absolute;inset:-60% -40%;background:linear-gradient(90deg,rgba(255,255,255,.05),rgba(255,255,255,.14),rgba(255,255,255,.05));filter:blur(36px);background-size:200% 100%;animation:ctaSweep 26s linear infinite}
        @keyframes ctaSweep{to{background-position:200% 50%}}
        .isl-cta .cta-wrap{position:relative;width:min(900px,92vw);margin:0 auto;text-align:center}
        .isl-cta h2{margin:0 0 10px;font-size:clamp(22px,4.6vw,40px)}
        .isl-cta p{margin:0 0 8px}
        .cta-buy{--btnPadY:12px;--btnPadX:26px;--btnR:999px;--shine:rgba(255,255,255,.35);position:relative;display:inline-flex;align-items:center;justify-content:center;padding:var(--btnPadY) var(--btnPadX);min-width:180px;border-radius:var(--btnR);background:linear-gradient(180deg,rgba(255,255,255,.10),rgba(255,255,255,.04));border:1px solid var(--line2);box-shadow:0 12px 30px rgba(0,0,0,.35), 0 0 0 1px var(--soft) inset, 0 0 24px rgba(255,255,255,.18);backdrop-filter:blur(4px);color:#fff;font-weight:700;letter-spacing:.4px;transform:translateZ(0);transition:transform .18s ease, box-shadow .18s ease, background .18s ease; cursor:pointer;}
        .cta-buy::before{content:"";position:absolute;inset:-2px;border-radius:inherit;background:radial-gradient(100% 60% at 50% 0%, var(--shine), transparent 60%);opacity:.45;filter:blur(18px);pointer-events:none;transition:opacity .2s ease}
        .cta-buy:hover{transform:scale(1.02);box-shadow:0 14px 36px rgba(0,0,0,.42), 0 0 0 1px var(--soft) inset, 0 0 34px rgba(255,255,255,.24)}
        .cta-buy:active{transform:scale(0.995)}
        .cta-buy:focus-visible{outline:0;box-shadow:0 0 0 2px rgba(255,255,255,.5), 0 0 0 6px rgba(255,255,255,.35)}

        /* JS-ready pre-states */
        .js-ready [data-reveal]{opacity:0;transform:translateY(24px) rotateX(6deg) scale(.985) translateZ(0);transform-style:preserve-3d}
        .js-ready [data-reveal-child]{opacity:0;transform:translateY(10px) translateZ(0)}

        /* Responsive */
        @media (max-width:980px){.isl-inside .i-body,.isl-why,.isl-works{grid-template-columns:1fr}.isl-why .w-hero{order:-1}.isl-how .t-card{grid-template-columns:1fr}.isl-how .t-rail{top:96px;height:min(62vh,520px)}}
        @media (max-width:520px){.isl-hero .h-title{font-size:clamp(28px,8vw,46px)}.isl-how .t-card{padding:12px}}

        html, body { -webkit-overflow-scrolling: touch; }

        @media (prefers-reduced-motion:reduce){
          .h-aurora .blob,.section-flow .line,.section-flow .flow-sheen,.cta-glow,.h-title.shine-run::after,.frame-shimmer.shimmer-run::after,.h-sparkles .sp,.h-shine .laser{animation:none!important}
        }
      `}</style>
    </main>
  );
}




